/**
 * GoodGuardian — iOS Push Notification registration hook
 *
 * Usage: call usePushNotifications() inside any authenticated component.
 * App.jsx calls it once after the user is confirmed logged in.
 *
 * What it does:
 *  1. Requests notification permission from the user (iOS prompt)
 *  2. Registers with APNs to get a device token
 *  3. POSTs the token to /api/notifications/register-token so the server
 *     can send pushes to this device when new messages arrive
 *  4. Sets up listeners for foreground notifications and taps
 */
import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { getToken } from '@/utils/auth'

// Lazy import — only resolved on native iOS; tree-shaken on web.
// @vite-ignore tells Rollup not to try bundling this native-only package.
async function getPushPlugin() {
  // eslint-disable-next-line import/no-unresolved
  const { PushNotifications } = await import(/* @vite-ignore */ '@capacitor/push-notifications')
  return PushNotifications
}

export function usePushNotifications() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    setupPush()
  }, [])
}

async function setupPush() {
  try {
    const Push = await getPushPlugin()

    // ── 1. Request permission ──────────────────────────────────────────────
    const { receive } = await Push.requestPermissions()
    if (receive !== 'granted') {
      console.log('[push] permission denied by user')
      return
    }

    // ── 2. Register with APNs ──────────────────────────────────────────────
    await Push.register()

    // ── 3. Receive token and send to server ───────────────────────────────
    await Push.addListener('registration', async ({ value: deviceToken }) => {
      console.log('[push] device token received:', deviceToken.slice(0, 8) + '…')
      const authToken = getToken()
      if (!authToken) return
      try {
        await fetch('/api/notifications/register-token', {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({ token: deviceToken, platform: 'ios' }),
        })
      } catch (err) {
        console.error('[push] failed to register token with server:', err)
      }
    })

    // ── 4. Registration error ─────────────────────────────────────────────
    await Push.addListener('registrationError', ({ error }) => {
      console.error('[push] APNs registration error:', error)
    })

    // ── 5. Foreground notification received ──────────────────────────────
    // The app is open — Capacitor surfaces the notification silently.
    // You could show an in-app banner here if desired.
    await Push.addListener('pushNotificationReceived', notification => {
      console.log('[push] foreground notification:', notification.title)
    })

    // ── 6. User tapped a notification ────────────────────────────────────
    await Push.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const screen = notification?.data?.screen
      if (screen) {
        // Use a small delay so the app is fully in foreground before navigating
        setTimeout(() => {
          window.location.hash = `/${screen}`
        }, 300)
      }
    })

  } catch (err) {
    console.error('[push] setup failed:', err)
  }
}
